/**
 * SF-4940: the bar above the Companies table while companies are ticked. Set one account type
 * on all of them, or schedule them all for deletion when every one is Demo or Test. Both ask
 * for confirmation first, naming how many companies and which.
 *
 * SF-5158: Mark inactive / Mark active switch them all off or on (the Active switch's rights),
 * and deletion stays off while any ticked company is active.
 */
import { useRef, useState } from 'react';
import { useDataProvider, useInvalidate } from '@refinedev/core';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Modal, Select, Space, Tooltip, Typography, message } from 'antd';
import { companyDeletionsQueryKey, type CompanyDeleteImpact } from '../api/companyDeleteClient';
import {
  BATCH_DELETABLE_TYPES,
  BATCH_SETTABLE_TYPES,
  allBatchDeletable,
  companyId,
  fetchDeleteImpact,
  isCompanyActive,
  scheduleDeletionForCompanies,
  schedulesDeletion,
  setAccountTypeForCompanies,
  setActiveForCompanies,
  type CompanyBatchResult,
} from '../companyBatch';
import { ACCOUNT_TYPE_LABELS, accountTypeLabel, type AccountType } from '../constants';
import type { Company } from '../list';

const companies = (n: number) => `${n} ${n === 1 ? 'company' : 'companies'}`;

type Action =
  | { kind: 'type'; accountType: AccountType }
  | { kind: 'delete' }
  | { kind: 'active'; isActive: boolean };

interface BatchRun {
  action: Action;
  /** Copied when the batch starts: the ticks can change while it runs. */
  targets: Company[];
}

interface Report {
  title: string;
  result: CompanyBatchResult;
  names: Map<string, string>;
  /** SF-5158: when set, each updated company is listed too, with this outcome. */
  doneLabel?: string;
}

interface CompaniesBatchBarProps {
  /** Ticked companies, as they were when ticked. */
  selected: Company[];
  canSetType: boolean;
  /** SF-5158: Mark inactive / Mark active, the company page's Active switch rights. */
  canSetActive: boolean;
  canDelete: boolean;
  /** Companies the batch changed, so the list can untick them. */
  onDone: (changedIds: string[]) => void;
  onClear: () => void;
}

/** One company's line in the deletion dialog: how many records go, or why it can't. */
function impactNote(
  q:
    | { isLoading: boolean; isFetching: boolean; isError: boolean; data?: CompanyDeleteImpact }
    | undefined,
) {
  if (!q || q.isLoading || q.isFetching) return ': checking…';
  if (q.isError) return ': could not check';
  if (!schedulesDeletion(q.data)) return ': this server deletes at once, use its own page';
  if (q.data?.deletionStatus || q.data?.deletion) return ': already waiting for deletion';
  if (q.data?.isActive === true) return ': still active, mark it inactive first';
  const total = q.data?.total ?? 0;
  return `: ${total.toLocaleString()} ${total === 1 ? 'record' : 'records'}`;
}

const actionTitle = (action: Action) => {
  if (action.kind === 'type') return `Changed to ${action.accountType}`;
  if (action.kind === 'active') return action.isActive ? 'Marked active' : 'Marked inactive';
  return 'Scheduled for deletion';
};

const activeVerb = (isActive: boolean) => (isActive ? 'active' : 'inactive');

export const CompaniesBatchBar: React.FC<CompaniesBatchBarProps> = ({
  selected,
  canSetType,
  canSetActive,
  canDelete,
  onDone,
  onClear,
}) => {
  const dataProvider = useDataProvider();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  // Set at once on confirm, before the mutation state re-renders, so a second confirm can't
  // start an overlapping run.
  const busy = useRef(false);

  // runBatch never rejects (each company's failure is in its result), so there is no onError.
  const mutation = useMutation({
    mutationFn: ({ action, targets }: BatchRun) => {
      const ids = targets.map(companyId);
      if (action.kind === 'active')
        return setActiveForCompanies(dataProvider(), ids, action.isActive);
      return action.kind === 'type'
        ? setAccountTypeForCompanies(dataProvider(), ids, action.accountType)
        : scheduleDeletionForCompanies(dataProvider(), ids);
    },
    onSuccess: (result, { action, targets }) => {
      // SF-5158: a company already in the asked-for state is settled too (its server copy said
      // so), so it is unticked like an updated one; a stale active tick would otherwise keep
      // blocking batch delete.
      const settled =
        action.kind === 'active'
          ? [...result.updated, ...result.skipped.map((s) => s.id)]
          : result.updated;
      if (settled.length > 0) {
        // resourceAll: a bare 'detail' has no id, so it would miss every cached company page.
        void invalidate({ resource: 'companies', invalidates: ['resourceAll'] });
        if (action.kind === 'delete') {
          void queryClient.invalidateQueries({ queryKey: companyDeletionsQueryKey });
        }
        onDone(settled);
      }
      const title = actionTitle(action);
      if (action.kind === 'active') {
        // Always a named report for Mark inactive / Mark active: every company and its outcome.
        const names = new Map(targets.map((c) => [companyId(c), c.name] as const));
        setReport({ title, result, names, doneLabel: title });
      } else if (result.failed.length === 0 && result.skipped.length === 0) {
        message.success(`${title}: ${companies(result.updated.length)}`);
      } else {
        const names = new Map(targets.map((c) => [companyId(c), c.name] as const));
        setReport({ title, result, names });
      }
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const running = mutation.isLoading;
  const count = selected.length;

  // The deletion dialog shows what each deletion affects, read fresh when it opens. Confirm
  // stays off until every answer is in and says this server schedules deletions.
  const impacts = useQueries({
    queries:
      confirming?.kind === 'delete'
        ? selected.map((c) => ({
            queryKey: ['sf-admin', 'batch-delete-impact', companyId(c)],
            queryFn: () => fetchDeleteImpact(dataProvider(), companyId(c)),
            staleTime: 0,
            retry: false,
            refetchOnWindowFocus: false,
          }))
        : [],
  });
  // isFetching too: reopening the dialog shows the last answers while fresh ones load, and an
  // old answer must not enable Confirm.
  const impactsLoading = impacts.some((q) => q.isLoading || q.isFetching);
  const impactsFailed = impacts.some((q) => q.isError);
  const impactsReady =
    impacts.length === count &&
    !impactsLoading &&
    !impactsFailed &&
    impacts.every(
      (q) =>
        schedulesDeletion(q.data) &&
        !q.data?.deletionStatus &&
        !q.data?.deletion &&
        q.data?.isActive !== true,
    );
  const graceDays = impactsReady ? impacts[0]?.data?.graceDays : undefined;
  const manualSteps = [...new Set(impacts.flatMap((q) => q.data?.manual_steps ?? []))];

  // Stays mounted while a batch runs or its report shows, even if every tick is cleared.
  if (count === 0 && !running && !report) return null;

  const typesDeletable = allBatchDeletable(selected);
  const notDeletable = selected.filter(
    (c) => !c.account_type || !BATCH_DELETABLE_TYPES.includes(c.account_type),
  );
  // SF-5158: only inactive companies can be deleted.
  const stillActive = selected.filter(isCompanyActive);
  const deletable = typesDeletable && stillActive.length === 0;
  const deleteBlock = !typesDeletable
    ? `Only Demo and Test companies can be deleted together. Untick: ${notDeletable
        .map((c) => c.name)
        .join(', ')}`
    : stillActive.length > 0
      ? `Still active: ${stillActive.map((c) => c.name).join(', ')}. Mark them inactive first.`
      : undefined;

  const confirm = () => {
    const action = confirming;
    setConfirming(null);
    if (!action || busy.current || selected.length === 0) return;
    if (
      action.kind === 'delete' &&
      (!allBatchDeletable(selected) || selected.some(isCompanyActive) || !impactsReady)
    ) {
      return;
    }
    busy.current = true;
    setReport(null);
    mutation.mutate({ action, targets: [...selected] });
  };

  const confirmTitle =
    confirming?.kind === 'type'
      ? `Change ${companies(count)} to ${confirming.accountType}?`
      : confirming?.kind === 'active'
        ? `Mark ${companies(count)} ${activeVerb(confirming.isActive)}?`
        : `Schedule ${companies(count)} for deletion?`;
  const okText =
    confirming?.kind === 'delete'
      ? 'Schedule deletion'
      : confirming?.kind === 'active'
        ? `Mark ${activeVerb(confirming.isActive)}`
        : 'Change account type';
  const intro =
    confirming?.kind === 'type'
      ? `You are going to change ${companies(count)} to ${confirming.accountType}:`
      : confirming?.kind === 'active'
        ? `You are going to mark ${companies(count)} ${activeVerb(confirming.isActive)}:`
        : `You are scheduling ${companies(count)} for deletion:`;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap align="center" aria-label="Batch actions">
        <Typography.Text strong>{companies(count)} selected</Typography.Text>
        {canSetType && (
          <>
            <Select
              aria-label="Account type for selected companies"
              placeholder="Choose an account type"
              options={BATCH_SETTABLE_TYPES.map((t) => ({
                value: t,
                label: ACCOUNT_TYPE_LABELS[t],
              }))}
              value={accountType}
              onChange={setAccountType}
              disabled={running}
              style={{ width: 200 }}
            />
            <Button
              disabled={!accountType || running || count === 0}
              loading={running}
              onClick={() => accountType && setConfirming({ kind: 'type', accountType })}
            >
              Set account type
            </Button>
          </>
        )}
        {canSetActive &&
          [false, true].map((isActive) => (
            <Button
              key={String(isActive)}
              disabled={running || count === 0}
              loading={running}
              onClick={() => setConfirming({ kind: 'active', isActive })}
            >
              Mark {activeVerb(isActive)}
            </Button>
          ))}
        {canDelete && (
          <Tooltip title={count > 0 ? deleteBlock : undefined}>
            <Button
              danger
              disabled={!deletable || running || count === 0}
              loading={running}
              onClick={() => setConfirming({ kind: 'delete' })}
            >
              Schedule deletion
            </Button>
          </Tooltip>
        )}
        {canDelete && count > 0 && !typesDeletable && (
          <Typography.Text type="secondary">
            Only Demo and Test companies can be deleted together.
          </Typography.Text>
        )}
        {canDelete && count > 0 && typesDeletable && stillActive.length > 0 && (
          <Typography.Text type="secondary">{deleteBlock}</Typography.Text>
        )}
        <Button type="link" onClick={onClear} disabled={running}>
          Clear selection
        </Button>
      </Space>
      {report && (
        <Alert
          type={
            report.result.failed.length === 0 && report.result.skipped.length === 0
              ? 'success'
              : 'warning'
          }
          showIcon
          closable
          onClose={() => setReport(null)}
          message={`${report.title}: ${companies(report.result.updated.length)} done, ${
            report.result.skipped.length
              ? `${companies(report.result.skipped.length)} skipped, `
              : ''
          }${companies(report.result.failed.length)} failed`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {report.doneLabel
                ? report.result.updated.map((id) => (
                    <li key={id}>
                      {report.names.get(id) ?? id}: {report.doneLabel}
                    </li>
                  ))
                : null}
              {[...report.result.failed, ...report.result.skipped].map((f) => (
                <li key={f.id}>
                  {report.names.get(f.id) ?? f.id}: {f.message}
                </li>
              ))}
            </ul>
          }
        />
      )}
      <Modal
        open={confirming !== null}
        title={confirmTitle}
        okText={okText}
        okButtonProps={{
          danger: confirming?.kind === 'delete',
          disabled: confirming?.kind === 'delete' && !impactsReady,
        }}
        onOk={confirm}
        onCancel={() => setConfirming(null)}
      >
        <Typography.Paragraph>{intro}</Typography.Paragraph>
        <ul style={{ maxHeight: 240, overflowY: 'auto', paddingLeft: 20 }}>
          {selected.map((c, i) => (
            <li key={companyId(c)}>
              {c.name}
              {c.account_type ? ` (${accountTypeLabel(c.account_type)})` : ''}
              {confirming?.kind === 'delete' && impactNote(impacts[i])}
            </li>
          ))}
        </ul>
        {confirming?.kind === 'delete' && (
          <>
            {impactsLoading && (
              <Typography.Paragraph>Checking what will be deleted…</Typography.Paragraph>
            )}
            {impactsFailed && (
              <Alert
                type="error"
                showIcon
                message="Could not check what will be deleted. Close this and try again."
              />
            )}
            {!impactsLoading && !impactsFailed && !impactsReady && (
              <Alert
                type="error"
                showIcon
                message="These can't be deleted together. Delete them from their own pages."
              />
            )}
            {impactsReady && (
              <Typography.Paragraph type="secondary">
                Each one is deleted after {graceDays} {graceDays === 1 ? 'day' : 'days'}. Until then
                you can cancel it from Deleted companies.
              </Typography.Paragraph>
            )}
            {manualSteps.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message="Still to do by hand after deletion"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {manualSteps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        )}
      </Modal>
    </Space>
  );
};
